import React, { useEffect, useState, createContext, useContext } from 'react';
import { translations, Language, TranslationKeys } from '../lib/translations';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function getNestedValue(obj: any, path: string): string {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return path; // Return the key path if not found
    }
  }
  return typeof result === 'string' ? result : path;
}
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('kmauk-language');
      if (saved && (saved === 'en' || saved === 'ps' || saved === 'ur')) {
        return saved as Language;
      }
    }
    return 'en';
  });
  const isRTL = language === 'ur';
  useEffect(() => {
    localStorage.setItem('kmauk-language', language);
    // Update document direction for RTL support
    if (typeof document !== 'undefined') {
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = language;
    }
  }, [language, isRTL]);
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };
  const t = (key: string): string => {
    return getNestedValue(translations[language], key);
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    isRTL
  }} data-id="element-149">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}